import { useCallback, useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AnimatePresence, motion } from "framer-motion";
import {
  Calendar,
  Check,
  ChevronDown,
  FileText,
  List,
  Loader2,
  Plus,
  Save,
  ToggleLeft,
  RotateCcw,
  Trash2,
  Type,
  X,
} from "lucide-react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { Button, Input, Textarea } from "@/components/ui/primitives";
import { ConfirmDialog } from "@/components/ui";
import { springs } from "@/components/ui/motion";
import { useApiMutation } from "@/packages/features-shared";
import { useTranslation } from "@/packages/utils";
import { cn } from "@/packages/utils";
import type { UIChecklistItem } from "@/packages/types";

/* ------------------------------------------------------------------ */
/* Schema                                                              */
/* ------------------------------------------------------------------ */

const checklistEditSchema = z.object({
  description: z.string().min(1),
  status: z.enum(["MISSING", "UPLOADED", "APPROVED"]),
  required: z.boolean(),
  isAnnex: z.boolean(),
  notes: z.string().optional(),
  clauseRefs: z.array(z.object({ value: z.string() })),
  questions: z.array(
    z.object({
      text: z.string().min(1),
      type: z.enum(["yes_no", "date", "document", "text", "selection"]),
      required: z.boolean(),
      order: z.number(),
      depthTier: z.enum(["basic", "standard", "deep-dive"]).optional(),
    }),
  ),
});

type ChecklistEditFormValues = z.infer<typeof checklistEditSchema>;

/* ------------------------------------------------------------------ */
/* Status helpers                                                      */
/* ------------------------------------------------------------------ */

type StatusValue = "MISSING" | "UPLOADED" | "APPROVED";

interface StatusOption {
  value: StatusValue;
  color: string;
  label: string;
}

const STATUS_COLORS: Record<StatusValue, string> = {
  MISSING: "bg-gray-400",
  UPLOADED: "bg-blue-400",
  APPROVED: "bg-green-400",
};

/* ------------------------------------------------------------------ */
/* Question type config                                                */
/* ------------------------------------------------------------------ */

const QUESTION_TYPES = [
  { value: "text" as const, icon: Type, label: "Text" },
  { value: "yes_no" as const, icon: ToggleLeft, label: "Yes/No" },
  { value: "date" as const, icon: Calendar, label: "Date" },
  { value: "document" as const, icon: FileText, label: "Document" },
  { value: "selection" as const, icon: List, label: "Selection" },
];

/* ------------------------------------------------------------------ */
/* Toggle Switch                                                       */
/* ------------------------------------------------------------------ */

function ToggleSwitch({
  checked,
  onChange,
  id,
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
  id?: string;
}) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200",
        checked ? "bg-accent" : "bg-border",
      )}
    >
      <span
        className={cn(
          "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200",
          checked ? "translate-x-4" : "translate-x-0",
        )}
      />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Collapsible Section                                                 */
/* ------------------------------------------------------------------ */

function CollapsibleSection({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-4 py-3 text-sm font-semibold hover:bg-muted/30 transition-colors rounded-lg"
      >
        <motion.span
          animate={{ rotate: open ? 0 : -90 }}
          transition={springs.snappy}
          className="shrink-0"
        >
          <ChevronDown className="h-4 w-4" />
        </motion.span>
        {title}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={springs.smooth}
            style={{ overflow: "hidden" }}
            onAnimationComplete={(definition) => {
              // After expand animation, allow overflow for focus rings
              if (
                typeof definition === "object" &&
                definition !== null &&
                "height" in definition &&
                definition.height === "auto"
              ) {
                // Handled by CSS after animation
              }
            }}
          >
            <div className="px-4 pb-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Status Dropdown                                                     */
/* ------------------------------------------------------------------ */

function StatusDropdown({
  value,
  onChange,
  options,
}: {
  value: StatusValue;
  onChange: (val: StatusValue) => void;
  options: StatusOption[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = options.find((s) => s.value === value) ?? options[0];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-muted/30 transition-colors"
      >
        <span className={cn("h-2 w-2 rounded-full", current.color)} />
        {current.label}
        <ChevronDown
          className={cn("h-3 w-3 transition-transform", open && "rotate-180")}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full z-50 mt-1 min-w-[160px] rounded-lg border border-border bg-card p-1 shadow-lg"
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                  opt.value === value
                    ? "bg-accent/10 text-accent-foreground"
                    : "hover:bg-muted/50",
                )}
              >
                <span className={cn("h-2 w-2 rounded-full", opt.color)} />
                {opt.label}
                {opt.value === value && (
                  <Check className="ml-auto h-3.5 w-3.5" />
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Question Type Selector                                              */
/* ------------------------------------------------------------------ */

function QuestionTypeSelector({
  value,
  onChange,
}: {
  value: string;
  onChange: (
    val: "yes_no" | "date" | "document" | "text" | "selection",
  ) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current =
    QUESTION_TYPES.find((qt) => qt.value === value) ?? QUESTION_TYPES[0];
  const CurrentIcon = current.icon;

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-2.5 py-1 text-xs hover:bg-muted/30 transition-colors"
      >
        <CurrentIcon className="h-3.5 w-3.5 text-muted-foreground" />
        {current.label}
        <ChevronDown
          className={cn("h-3 w-3 transition-transform", open && "rotate-180")}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full z-50 mt-1 min-w-[150px] rounded-lg border border-border bg-card p-1 shadow-lg"
          >
            {QUESTION_TYPES.map((qt) => {
              const Icon = qt.icon;
              return (
                <button
                  key={qt.value}
                  type="button"
                  onClick={() => {
                    onChange(qt.value);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-xs transition-colors",
                    qt.value === value
                      ? "bg-accent/10 text-accent-foreground"
                      : "hover:bg-muted/50",
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {qt.label}
                  {qt.value === value && <Check className="ml-auto h-3 w-3" />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Clause Ref Tags                                                     */
/* ------------------------------------------------------------------ */

function ClauseRefTags({
  refs,
  onRemove,
  onAdd,
  placeholder,
}: {
  refs: Array<{ id: string; value: string }>;
  onRemove: (index: number) => void;
  onAdd: (value: string) => void;
  placeholder: string;
}) {
  const [inputValue, setInputValue] = useState("");

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (trimmed) {
        onAdd(trimmed);
        setInputValue("");
      }
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {refs.map((ref, index) => (
          <span
            key={ref.id}
            className="inline-flex items-center gap-1 rounded-full border border-border/50 bg-muted/30 px-2.5 py-0.5 text-xs"
          >
            {ref.value}
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="hover:text-destructive transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <Input
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="text-sm"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main Component                                                      */
/* ------------------------------------------------------------------ */

interface ChecklistEditPanelProps {
  item: UIChecklistItem;
  auditId: Id<"audits">;
  onDirtyChange?: (dirty: boolean) => void;
}

export function ChecklistEditPanel({
  item,
  onDirtyChange,
}: ChecklistEditPanelProps) {
  const { t } = useTranslation(["audits", "common"]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [generalOpen, setGeneralOpen] = useState(true);
  const [questionsOpen, setQuestionsOpen] = useState(true);
  const [clauseRefsOpen, setClauseRefsOpen] = useState(true);
  const [showSaved, setShowSaved] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const updateMutation = useApiMutation(
    api.checklist_mutations.updateChecklistItemDetails,
    {
      name: "update-checklist-item",
      successMessage: t("checklist.detail.requirementUpdated"),
    },
  );

  const deleteMutation = useApiMutation(
    api.checklist_mutations.removeChecklistItem,
    {
      name: "delete-checklist-item",
      successMessage: t("checklist.edit.itemDeleted"),
    },
  );

  const restoreMutation = useApiMutation(
    api.checklist_mutations.restoreChecklistItem,
    {
      name: "restore-checklist-item",
      successMessage: t("checklist.edit.itemRestored"),
    },
  );

  const form = useForm<ChecklistEditFormValues>({
    resolver: zodResolver(checklistEditSchema),
    defaultValues: {
      description: item.description ?? "",
      status: item.status as "MISSING" | "UPLOADED" | "APPROVED", // UIChecklistItem.status is string; narrowing for form schema
      required: item.required,
      isAnnex: item.isAnnex ?? false,
      notes: item.notes ?? "",
      clauseRefs: (item.clauseRefs ?? []).map((v) => ({ value: v })),
      questions: (item.questions ?? []).map((q) => ({
        text: q.text,
        type: q.type,
        required: q.required,
        order: q.order,
        depthTier: q.depthTier,
      })),
    },
  });

  const {
    fields: clauseFields,
    append: appendClause,
    remove: removeClause,
  } = useFieldArray({ control: form.control, name: "clauseRefs" });

  const {
    fields: questionFields,
    append: appendQuestion,
    remove: removeQuestion,
  } = useFieldArray({ control: form.control, name: "questions" });

  // Reset form when item changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: item.id is an intentional proxy; resetting on full item would cause infinite loops with nested objects
  useEffect(() => {
    form.reset({
      description: item.description ?? "",
      status: item.status as "MISSING" | "UPLOADED" | "APPROVED", // UIChecklistItem.status is string; narrowing for form schema
      required: item.required,
      isAnnex: item.isAnnex ?? false,
      notes: item.notes ?? "",
      clauseRefs: (item.clauseRefs ?? []).map((v) => ({ value: v })),
      questions: (item.questions ?? []).map((q) => ({
        text: q.text,
        type: q.type,
        required: q.required,
        order: q.order,
        depthTier: q.depthTier,
      })),
    });
  }, [item.id, form]);

  // Report dirty state
  const isDirty = form.formState.isDirty;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Cmd+S / Ctrl+S keyboard shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        if (isDirty) {
          void form.handleSubmit(handleSave)();
        }
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  });

  const handleSave = useCallback(
    async (values: ChecklistEditFormValues) => {
      // Boundary cast: UIChecklistItem.id is a branded ChecklistItemId, not a raw Convex Id
      // This cast is required at the Convex mutation boundary
      await updateMutation.mutateAsync({
        checklistItemId: item.id as Id<"checklistItems">,
        description: values.description,
        status: values.status,
        required: values.required,
        isAnnex: values.isAnnex,
        notes: values.notes || undefined,
        clauseRefs: values.clauseRefs.map((r) => r.value),
        questions: values.questions,
      });
      form.reset(values);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    },
    [item.id, updateMutation, form],
  );

  const handleDelete = useCallback(async () => {
    // Boundary cast: UIChecklistItem.id is a branded ChecklistItemId, not a raw Convex Id
    await deleteMutation.mutateAsync({
      checklistItemId: item.id as Id<"checklistItems">,
    });
    setShowDeleteConfirm(false);
  }, [item.id, deleteMutation]);

  const handleRestore = useCallback(async () => {
    await restoreMutation.mutateAsync({
      checklistItemId: item.id as Id<"checklistItems">,
    });
  }, [item.id, restoreMutation]);

  const isDeleted = !!item.deletedAt;
  const isSaving = updateMutation.isPending;
  const isDeleting = deleteMutation.isPending;
  const isRestoring = restoreMutation.isPending;
  const watchedStatus = form.watch("status");

  const statusOptions: StatusOption[] = [
    {
      value: "MISSING",
      color: STATUS_COLORS.MISSING,
      label: t("checklist.status.missing"),
    },
    {
      value: "UPLOADED",
      color: STATUS_COLORS.UPLOADED,
      label: t("checklist.status.uploaded"),
    },
    {
      value: "APPROVED",
      color: STATUS_COLORS.APPROVED,
      label: t("checklist.status.approved"),
    },
  ];

  return (
    <div className="flex h-full flex-col">
      {/* Deleted banner */}
      {isDeleted && (
        <div className="flex items-center justify-between gap-2 border-b border-destructive/20 bg-destructive/5 px-4 py-2">
          <span className="text-sm text-destructive font-medium">
            {t("checklist.edit.deletedBanner")}
          </span>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            disabled={isRestoring}
            onClick={() => void handleRestore()}
          >
            {isRestoring ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
            )}
            {t("checklist.edit.restore")}
          </Button>
        </div>
      )}

      {/* Sticky toolbar */}
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b bg-background px-4 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span
            className={cn(
              "truncate text-base font-semibold",
              isDeleted && "line-through text-muted-foreground",
            )}
          >
            {item.name}
          </span>
          {isDirty && !isDeleted && (
            <span
              className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-orange-400"
              title={t("checklist.edit.unsavedChanges")}
            />
          )}
        </div>
        {!isDeleted && (
          <>
            <StatusDropdown
              value={watchedStatus}
              onChange={(val) =>
                form.setValue("status", val, { shouldDirty: true })
              }
              options={statusOptions}
            />
            <Button
              variant="ghost"
              size="sm"
              type="button"
              disabled={isDeleting || isSaving}
              onClick={() => setShowDeleteConfirm(true)}
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
            <Button
              size="sm"
              type="button"
              disabled={isSaving || !isDirty}
              onClick={() => void form.handleSubmit(handleSave)()}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : showSaved ? (
                <Check className="h-4 w-4" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span className="ml-1">
                {showSaved
                  ? t("checklist.edit.saved")
                  : t("common:buttons.save")}
              </span>
            </Button>
          </>
        )}
      </div>

      {/* Scrollable form */}
      <div
        className={cn(
          "flex-1 overflow-y-auto px-4 py-4",
          isDeleted && "pointer-events-none opacity-40",
        )}
      >
        <form
          ref={formRef}
          onSubmit={(e) => {
            e.preventDefault();
            void form.handleSubmit(handleSave)();
          }}
          className="space-y-4"
        >
          {/* General section */}
          <CollapsibleSection
            title={t("checklist.edit.generalSection")}
            open={generalOpen}
            onToggle={() => setGeneralOpen(!generalOpen)}
          >
            <div className="space-y-1">
              {/* Description */}
              <div className="space-y-1 pb-3">
                <label
                  htmlFor="edit-description"
                  className="text-sm font-medium text-muted-foreground"
                >
                  {t("checklist.detail.requirementLabel")}
                </label>
                <Textarea
                  id="edit-description"
                  {...form.register("description")}
                  rows={3}
                  placeholder={t("checklist.detail.requirementPlaceholder")}
                  className={cn(
                    form.formState.errors.description && "border-destructive",
                  )}
                />
                {form.formState.errors.description && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.description.message}
                  </p>
                )}
              </div>

              {/* Required — property row */}
              <div className="flex items-center justify-between border-b border-border/50 py-2">
                {/* biome-ignore lint/a11y/noLabelWithoutControl: Toggle switch is a custom button-based control */}
                <label className="text-sm text-muted-foreground">
                  {t("checklist.edit.requiredLabel")}
                </label>
                <ToggleSwitch
                  checked={form.watch("required")}
                  onChange={(val) =>
                    form.setValue("required", val, { shouldDirty: true })
                  }
                />
              </div>

              {/* Annex — property row */}
              <div className="flex items-center justify-between border-b border-border/50 py-2">
                {/* biome-ignore lint/a11y/noLabelWithoutControl: Toggle switch is a custom button-based control */}
                <label className="text-sm text-muted-foreground">
                  {t("checklist.edit.annexLabel")}
                </label>
                <ToggleSwitch
                  checked={form.watch("isAnnex")}
                  onChange={(val) =>
                    form.setValue("isAnnex", val, { shouldDirty: true })
                  }
                />
              </div>

              {/* Notes — expandable textarea */}
              <div className="pt-2">
                <label
                  htmlFor="edit-notes"
                  className="text-sm text-muted-foreground"
                >
                  {t("checklist.detail.notesLabel")}
                </label>
                <Textarea
                  id="edit-notes"
                  {...form.register("notes")}
                  rows={1}
                  placeholder={t("checklist.detail.contextPlaceholder")}
                  className="mt-1 resize-none transition-all focus:min-h-[80px]"
                  onFocus={(e) => {
                    if (e.target.scrollHeight > e.target.clientHeight) {
                      e.target.style.height = `${e.target.scrollHeight}px`;
                    }
                  }}
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* Questions section */}
          <CollapsibleSection
            title={t("checklist.edit.questionsCount", {
              count: questionFields.length,
            })}
            open={questionsOpen}
            onToggle={() => setQuestionsOpen(!questionsOpen)}
          >
            <div className="space-y-3">
              {questionFields.length === 0 && (
                <p className="py-2 text-center text-sm text-muted-foreground">
                  {t("checklist.edit.noQuestions")}
                </p>
              )}
              {questionFields.map((field, index) => (
                <div
                  key={field.id}
                  className="group rounded-md border border-border/60 p-3 transition-shadow hover:shadow-sm space-y-2"
                >
                  <Textarea
                    {...form.register(`questions.${index}.text`)}
                    rows={2}
                    placeholder={t("checklist.edit.questionTextPlaceholder")}
                    className={cn(
                      "border-transparent bg-transparent focus:border-input focus:bg-background transition-colors resize-none",
                      form.formState.errors.questions?.[index]?.text &&
                        "border-destructive",
                    )}
                  />
                  <div className="flex items-center gap-2">
                    <QuestionTypeSelector
                      value={form.watch(`questions.${index}.type`)}
                      onChange={(val) =>
                        form.setValue(`questions.${index}.type`, val, {
                          shouldDirty: true,
                        })
                      }
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const current = form.getValues(
                          `questions.${index}.required`,
                        );
                        form.setValue(`questions.${index}.required`, !current, {
                          shouldDirty: true,
                        });
                      }}
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs border transition-colors",
                        form.watch(`questions.${index}.required`)
                          ? "border-accent bg-accent/10 text-accent-foreground"
                          : "border-border text-muted-foreground hover:border-accent/50",
                      )}
                    >
                      {t("checklist.edit.requiredLabel")}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeQuestion(index)}
                      className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() =>
                  appendQuestion({
                    text: "",
                    type: "yes_no",
                    required: false,
                    order: questionFields.length + 1,
                  })
                }
                className="gap-1"
              >
                <Plus className="h-4 w-4" />
                {t("checklist.edit.addQuestion")}
              </Button>
            </div>
          </CollapsibleSection>

          {/* Clause References section */}
          <CollapsibleSection
            title={t("checklist.edit.clauseRefsCount", {
              count: clauseFields.length,
            })}
            open={clauseRefsOpen}
            onToggle={() => setClauseRefsOpen(!clauseRefsOpen)}
          >
            <ClauseRefTags
              refs={clauseFields.map((f, i) => ({
                id: f.id,
                value: form.watch(`clauseRefs.${i}.value`) || f.value,
              }))}
              onRemove={(index) => removeClause(index)}
              onAdd={(value) => appendClause({ value })}
              placeholder={t("checklist.edit.addClauseRefPlaceholder")}
            />
          </CollapsibleSection>
        </form>
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <ConfirmDialog.Title>
          {t("checklist.edit.deleteTitle")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          {t("checklist.edit.deleteDescription")}
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={() => void handleDelete()}
          onCancel={() => setShowDeleteConfirm(false)}
          variant="danger"
          confirmLabel={t("common:buttons.delete")}
        />
      </ConfirmDialog>
    </div>
  );
}
